import React, { useEffect, useState } from "react";
import { Check, Copy, ExternalLink, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import { createTelegramLink } from "../../../services/strategyDeployApi";

const POLL_MS = 3000;
const GIVE_UP_MS = 300_000;

/** `https://t.me/<bot>?start=<code>` → bot name, the message that links the chat, and the
 *  `tg://` link the installed Telegram app opens directly (the t.me page's hand-off often fails). */
function parseDeeplink(url: string): { bot: string; command: string; appUrl: string } | null {
  try {
    const u = new URL(url);
    const bot = u.pathname.replace(/^\/+/, "");
    const code = u.searchParams.get("start");
    return bot && code
      ? {
          bot,
          command: `/start ${code}`,
          appUrl: `tg://resolve?domain=${encodeURIComponent(bot)}&start=${encodeURIComponent(code)}`,
        }
      : null;
  } catch {
    return null;
  }
}

/**
 * Opens the Tessera bot in Telegram with a one-time link. While waiting it re-checks every few
 * seconds (and on focus) via `onReturn`; the parent unmounts this once linked. A small panel
 * shows the fallback — the exact message to send the bot — for when the Telegram app doesn't
 * open from the browser.
 */
export function ConnectTelegramButton({
  onReturn,
  size = "xs",
  variant = "outline-brand",
  label = "Connect Telegram",
}: {
  onReturn: () => void;
  size?: "xs" | "sm";
  variant?: "outline-brand" | "gradient";
  label?: string;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const waiting = link != null;
  const parsed = link ? parseDeeplink(link) : null;

  useEffect(() => {
    if (!waiting) return;
    const back = () => onReturn();
    window.addEventListener("focus", back);
    const poll = window.setInterval(back, POLL_MS);
    const giveUp = window.setTimeout(() => {
      setLink(null);
      toast.message("Telegram didn't confirm yet", { description: "Click Connect to get a fresh link and try again." });
    }, GIVE_UP_MS);
    return () => {
      window.removeEventListener("focus", back);
      window.clearInterval(poll);
      window.clearTimeout(giveUp);
    };
  }, [waiting, onReturn]);

  const connect = async () => {
    setBusy(true);
    try {
      const url = await createTelegramLink();
      setLink(url);
      setCopied(false);
      // Straight to the Telegram app; the panel keeps the web link and the copy fallback.
      const app = parseDeeplink(url)?.appUrl;
      if (app) window.location.href = app;
      else window.open(url, "_blank", "noopener");
    } catch (e) {
      presentMutationError(e, "Could not connect Telegram");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!parsed) return;
    try {
      await navigator.clipboard.writeText(parsed.command);
      setCopied(true);
    } catch {
      toast.error("Could not copy. Select the text and copy it.");
    }
  };

  return (
    <Popover open={waiting} onOpenChange={(open) => (!open ? setLink(null) : undefined)}>
      <PopoverAnchor asChild>
        <Button
          type="button"
          size={size}
          variant={variant}
          data-font="primary"
          disabled={busy}
          data-telegram-connect=""
          onClick={() => void connect()}
        >
          {waiting || busy ? <Loader2 className="size-3 animate-spin" /> : <Send className="size-3" />}
          <span className="normal-case tracking-normal">{waiting ? "Waiting…" : label}</span>
        </Button>
      </PopoverAnchor>
      <PopoverContent align="end" className="w-[320px] p-4" data-telegram-connect-help="">
        <p className="font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">Finish in Telegram</p>
        <p className="mt-1 text-xs text-gray-500 dark:text-white">
          Telegram should open on the Tessera bot. Press <b>Start</b> there.
        </p>
        {parsed ? (
          <Button asChild size="sm" variant="gradient" data-font="primary" className="mt-3 w-full">
            <a href={parsed.appUrl}>
              <Send className="size-3.5" />
              <span className="normal-case tracking-normal">Open Telegram app</span>
            </a>
          </Button>
        ) : null}
        <p className="mt-3 text-xs text-gray-500 dark:text-white">
          Still nothing? In Telegram, search <b>@{parsed?.bot ?? "the Tessera bot"}</b> and send it this message:
        </p>
        {parsed ? (
          <div className="mt-3 flex items-center gap-2 rounded-md bg-muted px-2.5 py-2">
            <code className="min-w-0 flex-1 truncate text-xs text-gray-500 dark:text-white">{parsed.command}</code>
            <Button type="button" size="xs" variant="ghost" onClick={() => void copy()} aria-label="Copy message">
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            </Button>
          </div>
        ) : null}
        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-white">
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="size-3 animate-spin" aria-hidden /> Waiting for Telegram…
          </span>
          {link ? (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[color:var(--brand-tropical-surf)]"
            >
              Open in browser <ExternalLink className="size-3" />
            </a>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
